import { StackedAreaChart, StackedAreaChartWithMonths } from '@mnd-frontend/charts';
import { BrandReportGroupStatisticsBy } from '@mnd-frontend/graphql-schema';
import { useNavigate } from '@mnd-frontend/ui';
import { subYears } from 'date-fns';
import qs from 'qs';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import type { Report2VisibilityReachOverTimeFragment } from '../../../../generated/types';
import {
  useReportDynamicData,
  useReportEditable,
  useReportGraphHelpers,
  useReportStaticData,
  useReportTracking,
  useReportTranslation,
} from '../../hooks';
import { GraphCaption, HitsModal, type HitsModalDetails, SkeletonBarChart } from '../common';
import { GraphContainer } from '../common/GraphContainer';
import { ScrollContainer } from '../common/ScrollContainer';

export const ReachOverTimeGraph = ({
  reachOverTime,
  groupBy,
  loading,
}: {
  reachOverTime: Report2VisibilityReachOverTimeFragment['reach'];
  groupBy: BrandReportGroupStatisticsBy;
  loading: boolean;
}) => {
  const { t } = useReportTranslation();
  const { track } = useReportTracking();
  const [details, setDetails] = useState<HitsModalDetails | null>(null);
  const onAreaClick = useCallback(
    ({ details }: { details: HitsModalDetails }) => {
      track({
        event: 'Brand Reports: Clicked Reach Over Time graph',
      });
      setDetails(details);
    },
    [track],
  );
  const onModalClose = () => setDetails(null);
  const { compareToPreviousYear, fromDate, toDate, searchProfiles, mediaTypes } =
    useReportStaticData();
  const { monitorLink } = useReportEditable();
  const navigate = useNavigate();
  const { getLegendCaption, getPeriodLabel, getPeriodLabelLong } = useReportGraphHelpers();
  const legendCaptionCurrentYear = getLegendCaption({ fromDate, toDate });
  const legendCaptionPreviousYear = getLegendCaption({
    fromDate: subYears(fromDate, 1),
    toDate: subYears(toDate, 1),
  });
  const { brandColor } = useReportDynamicData();
  const { data, mappings } = useMemo(() => {
    const mappings: {
      name: string | ReactNode;
      dataKey: 'reach' | 'reachPreviousYear';
      color: string;
      detailsDataKey: 'details' | 'detailsPreviousYear';
    }[] = [
      {
        name: legendCaptionCurrentYear,
        dataKey: 'reach',
        detailsDataKey: 'details',
        color: brandColor,
      },
    ];
    if (compareToPreviousYear)
      mappings.unshift({
        name: legendCaptionPreviousYear,
        dataKey: 'reachPreviousYear',
        detailsDataKey: 'detailsPreviousYear',
        color: brandColor + '80',
      });
    return {
      data: reachOverTime.map(period => {
        const year = new Date(period.startAt).getFullYear();

        return {
          name: getPeriodLabel(groupBy, year, period.month, period.week),
          reach: period.reach,
          month: period.month || undefined,
          reachPreviousYear: period.reachPreviousYear ?? null,
          details: {
            startAt: period.startAt,
            endAt: period.endAt,
            label: getPeriodLabelLong(groupBy, year, period.month, period.week),
          },
          detailsPreviousYear: {
            startAt: period.startAtPreviousYear,
            endAt: period.endAtPreviousYear,
            label: getPeriodLabelLong(groupBy, year - 1, period.month, period.week),
          },
        };
      }),
      mappings,
    };
  }, [
    legendCaptionCurrentYear,
    brandColor,
    compareToPreviousYear,
    legendCaptionPreviousYear,
    reachOverTime,
    getPeriodLabel,
    groupBy,
    getPeriodLabelLong,
  ]);

  const onLinkClick = monitorLink
    ? (details?: HitsModalDetails) => {
        if (!details) return;
        const { startAt, endAt } = details;
        navigate({
          pathname: monitorLink,
          search: qs.stringify({
            time: 'range',
            from: startAt,
            to: endAt,
            profile: searchProfiles.map(s => s.id)?.join(','),
            source: mediaTypes?.map(mt => mt.toLowerCase()).join(','),
          }),
        });
      }
    : undefined;
  const showMonths = groupBy === BrandReportGroupStatisticsBy.Month;

  return (
    <ScrollContainer minWidth="35rem">
      <GraphContainer data-testid="reach-graph">
        {!loading && <GraphCaption>{t('reports_key_metrics_reach')}</GraphCaption>}
        {!loading &&
          (showMonths ? (
            <StackedAreaChartWithMonths
              height={450}
              data={data}
              stacked={false}
              legend={compareToPreviousYear}
              mappings={mappings}
              onAreaClick={onAreaClick}
            />
          ) : (
            <StackedAreaChart
              height={450}
              data={data}
              stacked={false}
              legend={compareToPreviousYear}
              mappings={mappings}
              staticBottomAxis
              onAreaClick={onAreaClick}
            />
          ))}
        {loading && <SkeletonBarChart />}
        {details ? (
          <HitsModal
            onLinkClick={onLinkClick ? () => onLinkClick(details) : undefined}
            onClose={onModalClose}
            details={details}
          />
        ) : null}
      </GraphContainer>
    </ScrollContainer>
  );
};
